import type { ComponentType } from "react"
import { lazy, Suspense } from "react"
import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useAtomDefaults } from "../contexts/atom-defaults-context"

/**
 * Lottie atom renderer — final Theme V2 M2 deferred atom (closes 46→47/47).
 *
 * Bundle strategy: lottie-react + lottie-web core are ~80KB gzipped, too
 * heavy to unconditionally include in the base renderer bundle. Lazy-load
 * via React.lazy so the Lottie runtime only resolves when an actual
 * LottieRenderer instance mounts. Pages without Lottie pay zero bundle
 * cost (Vite/rollup code-splits the dynamic import into its own chunk).
 *
 * Behavior defaults (loop=true, autoplay=true) are hardcoded — standard
 * web animation idiom. The theme layer does NOT override these: per the
 * default-theme.json matrix, atoms.lottie is {} (no variants, no sizes,
 * no per-theme loop/autoplay). Consumers flip the behavior via
 * element.loop / element.autoplay at the template level.
 *
 * Source shape: lottie-react's public React API only exposes
 * `animationData` (embedded JSON object), not `path` (URL) — the URL
 * variant lives on lottie-web's separate AnimationConfigWithPath which
 * this wrapper doesn't re-export. We therefore only accept `element.src`
 * as an embedded animation object. Consumers wanting URL-backed Lottie
 * either (a) pre-fetch the JSON and pass the parsed object, or (b) add
 * a follow-up renderer that wraps `useLottie` with an explicit loader.
 *
 * jsdom note: lottie-react renders into a canvas/SVG element that jsdom
 * doesn't implement. The outer <span> carries data-atom-type so tests
 * can still assert atom-type markers in DOM; the inner Lottie content
 * renders as empty in jsdom but mounts a real player in browsers.
 */

type LottieProps = {
  animationData: unknown
  loop?: boolean
  autoplay?: boolean
}

const Lottie = lazy(async () => {
  // Vite pre-bundles lottie-react (CJS → ESM interop) as a namespace whose
  // `default` is the CJS module object, not the React component itself —
  // the actual component lives at `mod.default.default`. Native ESM builds
  // expose the component directly on `mod.default`. Prefer the nested form
  // when present, fall back to the flat form otherwise.
  const mod = await import("lottie-react")
  const nested = (mod as unknown as { default?: { default?: unknown } }).default?.default
  const flat = (mod as unknown as { default?: unknown }).default
  const component = nested ?? flat
  return { default: component as ComponentType<LottieProps> }
})

const LOTTIE_DEFAULTS = {
  loop: true,
  autoplay: true,
} as const

type LottieElement = CogoElement & {
  loop?: boolean
  autoplay?: boolean
  src?: unknown
}

export function LottieRenderer({ element }: RendererProps) {
  const atomDefaults = useAtomDefaults("lottie")
  const resolvedVariant = element.variant ?? atomDefaults.defaultVariant ?? "default"

  const el = element as LottieElement
  const loop = el.loop ?? LOTTIE_DEFAULTS.loop
  const autoplay = el.autoplay ?? LOTTIE_DEFAULTS.autoplay
  // Only embedded JSON (object) is supported; see file header for rationale.
  const animationData = typeof el.src === "object" && el.src !== null ? el.src : null
  const unsupportedSource = typeof el.src === "string" && el.src.trim().length > 0

  return (
    <span
      data-testid={`lottie-${element.id}`}
      data-atom-type="lottie"
      data-variant={resolvedVariant}
      data-loop={String(loop)}
      data-autoplay={String(autoplay)}
      className={element.className}
    >
      {animationData !== null ? (
        <Suspense fallback={null}>
          <Lottie
            animationData={animationData}
            loop={loop}
            autoplay={autoplay}
          />
        </Suspense>
      ) : unsupportedSource ? (
        <span role="alert" className="inline-flex min-h-8 items-center rounded border border-destructive px-2 text-sm text-destructive">
          Lottie URL sources are unsupported; embed animation JSON.
        </span>
      ) : (
        <span role="alert" className="inline-flex min-h-8 items-center rounded border border-dashed px-2 text-sm text-muted-foreground">
          Missing Lottie animation data.
        </span>
      )}
    </span>
  )
}
